import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { useSelector } from "react-redux";
import selectors from "../../../modules/selectors/datePickerSelectors";
import { CREATIVE_TYPE, YEAR } from "../Constants";
import { fetchCreativesData, generateFilterObj, getCurrentMonth } from "../services/CreativeServices";

export const useCreativeHook = () => {
  const { userStores = [] } = useSelector((state) => state.auth);
  const { transformedDataWithDate } = useSelector(selectors?.datePickerCombinedData);
  const currentYear = transformedDataWithDate?.[0]?.currYear;// to be used to calculate the year list 
  const navigate = useNavigate();
  const options = useMemo(() => {
    const stores = userStores?.map(store => ({
      label: store.branch_name,
      value: store.branch_number,
      ...store,
    }));
    return [...stores.filter(store => store.value !== 1 && !store?.hide_creative_branch).sort((a, b) => a.label.localeCompare(b.label))];
  }, [userStores]);
  const [filters, setFilters] = useState({
    branch: '',
    year: '',
    month: '',
    creative_type: '',
  });
  const [resetFilter, setResetFilter] = useState(false);
  const [isLoading, setLoading] = useState(false);// to be used when API call is integrated 
  const [error, setError] = useState(null);// to be used whrn API call is integrated
  const [creativeData, setCreativeData] = useState([]);
  const location = useLocation();
  const yearsList = useRef(null);
  const currentMonth = useRef(null);
  const initialLoading = useRef(true);
  const searchParams = new URLSearchParams(location.search);
  const urlMonth = searchParams.get('month');
  const urlYear = searchParams.get('year');
  const urlStoreId = searchParams.get('store_id');
  const urlCreativeType = searchParams.get('creative_type');

  // Function to update the specific filter
  const handleFilterChange = (value, filterType) => {
    setFilters(prevFilters => ({
      ...prevFilters,
      [filterType]: value,
    }));
  };

  const handleResetForFilters = () => {
    setResetFilter(true)
  }

  const fetchData = async (appliedFilters) => {
    try {
      setLoading(true);
      const response = await fetchCreativesData(appliedFilters);
      setCreativeData(response);
      setLoading(false);
    } catch (error) {
      setCreativeData([]);
      setError(error);
      setLoading(false);
    }
  }

  useEffect(() => {
    if (transformedDataWithDate?.length > 0 && initialLoading.current) {
      // YEAR is hard coded in filter because there is data only for 2024 and above 
      yearsList.current = Array.from({ length: transformedDataWithDate?.length }, (_, i) => ({
        label: `${currentYear - i}`,
        value: currentYear - i,
      }))?.filter(data => data?.value >= YEAR);

      // for setting the default filters 
      currentMonth.current = getCurrentMonth();
      const initialFiltersObj = {
        branch: parseInt(urlStoreId) || options[0]?.value,
        year: parseInt(urlYear) || yearsList?.current[0]?.value,
        month: parseInt(urlMonth) || currentMonth?.current,
        creative_type: urlCreativeType || CREATIVE_TYPE[0]?.value,
      }
      setFilters(initialFiltersObj);

      const filterObj = generateFilterObj(initialFiltersObj);
      // initial API call to be made here
      !!CREATIVE_TYPE[0]?.value && !isLoading && fetchData(filterObj);

      initialLoading.current = false;
    } else if (!isLoading && !initialLoading.current) {
      // after the initial filter call is over then make API call here on filter change
      const filterObj = generateFilterObj(filters);
      !!filters?.creative_type && fetchData(filterObj);
    }
  }, [transformedDataWithDate, currentYear, options, filters]);

  useEffect(() => {
    try {
      if (!initialLoading.current && !isLoading) {
        const params = new URLSearchParams();
        Object?.keys(filters)?.forEach(key => {
          if (filters?.[key]) params.set(key, filters?.[key]);
        });
        navigate({ search: params.toString() }, { replace: true });
      }
    } catch (error) {
      console.error("error in setting the filter values in creatives--->", error);
    }
  }, [filters, navigate]);

  return {
    userStores, options, isLoading, error, handleResetForFilters, resetFilter, handleFilterChange, filters, yearsList, currentMonth, initialLoading,
    creativeData, urlMonth, urlYear, urlStoreId, urlCreativeType,
  }
}